/**
 * Features Showcase Widget
 * Parses product description HTML to display standout features and searchable technical specifications
 *
 * Features:
 * - Extracts "Standout Features" from blockquote sections (icon-only cards)
 * - Parses h4 category headings with nested li attribute lists
 * - Auto-matches icons for 50+ technical attributes
 * - Live search filtering (labels AND values)
 * - Category filter buttons for quick navigation
 * - Horizontal scroll cards on mobile
 * - Theme token styling with British English
 *
 * HTML Pattern Expected:
 * <h3>Standout Features</h3>
 * <blockquote><span>LCD Dash Screen</span></blockquote>
 * <blockquote><span>Remote Control</span></blockquote>
 *
 * <h4>Speed & Performance</h4>
 * <li><strong>Top Speed:</strong> 6 km/h</li>
 * <li><strong>Battery:</strong> 12V 7Ah</li>
 *
 * @version 1.0.0
 */

(function(window) {
  'use strict';

  class FeaturesShowcaseWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.productData = {
        description: config.productDescription || manager.productData.description || '',
        title: config.productTitle || manager.productData.title || ''
      };

      this.parsedData = null;
      this.standoutFeatures = [];
      this.attributeCategories = [];
      this.filteredCategories = [];
      this.searchTerm = '';
      this.selectedCategory = 'all'; // Track currently selected category for navigation
      this.searchDebounceTimeout = null;

      // Read More state
      this.isExpanded = false;
      this.collapsedHeight = 400; // Show ~400px of content before "Read More"
      this.toggleButtonClickHandler = null;

      // Icon mapping configuration - using valid Tabler Icons
      this.ICON_MAP = {
        // Speed & Performance
        'speed|km/h|mph|fast': 'gauge',
        'battery|voltage|power|charge|ah': 'battery-charging',
        'motor|engine': 'engine',
        'gear|transmission': 'settings',
        'cylinder': 'circle',
        'stroke': 'wave-sine',
        'cool|temperature': 'temperature',
        'petrol|fuel|gas': 'gas-station',

        // Suspension & Brakes
        'suspension|shock|fork': 'arrow-autofit-height',
        'brake': 'hand-stop',
        'wheel|tire|tyre': 'circle-dotted',

        // Dimensions & Weight
        'weight|kg|load|capacity': 'scale',
        'dimension|size|height|width|length': 'ruler-2',
        'seat|seater': 'armchair',

        // Media & Connectivity
        'usb': 'usb',
        'bluetooth': 'bluetooth',
        'radio|fm|am': 'radio',
        'music|mp3|audio|sound': 'music',
        'speaker': 'volume',
        'volume': 'volume',

        // Lights & Safety
        'light|led|headlight|brightness': 'bulb',
        'safety|belt|harness': 'shield-check',
        'remote|control|parental': 'device-remote',
        'door|opening': 'door',
        'key|start|ignition': 'key',
        'horn': 'bell-ringing',
        'lock': 'lock',

        // Materials & Build
        'material|frame|body|plastic|pp': 'box',
        'steel|metal': 'square',
        'colour|color|paint': 'palette',

        // Age & Recommendations
        'age|years|recommended': 'user',
        'riiroo': 'star',

        // Other
        'assembly|tools': 'tool',
        'delivery|shipping': 'truck-delivery',
        'warranty|guarantee': 'shield',
        'brand': 'award',
        'mode|low|high': 'adjustments',

        // Fallback
        'default': 'list-details'
      };
    }

    /**
     * Initialise widget - parse description HTML
     */
    async init() {
      try {
        // Parse description HTML
        this.parsedData = this.parseDescriptionHTML(this.productData.description);

        // Extract standout features and attribute categories
        this.standoutFeatures = this.parsedData.standoutFeatures;
        this.attributeCategories = this.parsedData.attributeCategories;

        // Initialise filtered categories (show all by default)
        this.filteredCategories = [...this.attributeCategories];

      } catch (error) {
        console.error('[FeaturesShowcaseWidget] Initialisation failed:', error);
      }
    }

    /**
     * Parse product description HTML to extract features and attributes
     * @param {string} html - Raw HTML from product description
     * @returns {Object} { standoutFeatures: [], attributeCategories: [] }
     */
    parseDescriptionHTML(html) {
      if (!html) {
        return { standoutFeatures: [], attributeCategories: [] };
      }

      // Create a temporary DOM element to parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract standout features
      const standoutFeatures = this.extractStandoutFeatures(doc);

      // Extract attribute categories
      const attributeCategories = this.extractAttributeCategories(doc);

      return {
        standoutFeatures,
        attributeCategories
      };
    }

    /**
     * Extract Standout Features from blockquote sections
     * Pattern: Find h3 with "Standout Features" text, then extract blockquotes until next h3/h4
     * @param {Document} doc - Parsed HTML document
     * @returns {Array} [{ name: 'LCD Dash Screen', icon: 'device-laptop' }, ...]
     */
    extractStandoutFeatures(doc) {
      const features = [];

      // Find h3 containing "Standout Features"
      const headings = Array.from(doc.querySelectorAll('h3'));
      const standoutHeading = headings.find(h =>
        h.textContent.toLowerCase().includes('standout') &&
        h.textContent.toLowerCase().includes('feature')
      );

      if (!standoutHeading) {
        return features;
      }

      // Collect blockquotes until next h3 or h4
      let currentNode = standoutHeading.nextElementSibling;

      while (currentNode) {
        const tagName = currentNode.tagName.toLowerCase();

        // Stop if we hit another heading
        if (tagName === 'h3' || tagName === 'h4') {
          break;
        }

        // Extract feature from blockquote (direct sibling)
        if (tagName === 'blockquote') {
          const span = currentNode.querySelector('span');
          const featureName = span ? span.textContent.trim() : currentNode.textContent.trim();

          if (featureName) {
            features.push({
              name: featureName,
              icon: this.getIconForAttribute(featureName)
            });
          }
        }

        // Extract features from blockquotes nested in lists
        if (tagName === 'ul' || tagName === 'ol') {
          const blockquotes = currentNode.querySelectorAll('blockquote');
          blockquotes.forEach(blockquote => {
            const span = blockquote.querySelector('span');
            const featureName = span ? span.textContent.trim() : blockquote.textContent.trim();

            if (featureName) {
              features.push({
                name: featureName,
                icon: this.getIconForAttribute(featureName)
              });
            }
          });
        }

        currentNode = currentNode.nextElementSibling;
      }

      return features;
    }

    /**
     * Extract Attribute Categories from h4 headings with li lists
     * Pattern: Find h4 headers, extract all li items until next h3/h4
     * Parse pattern: "Label: Value" or "<strong>Label:</strong> Value"
     * @param {Document} doc - Parsed HTML document
     * @returns {Array} [{ name: 'Speed & Performance', attributes: [{label, value, icon}] }]
     */
    extractAttributeCategories(doc) {
      const categories = [];

      // Find all h4 headings
      const h4Headings = Array.from(doc.querySelectorAll('h4'));

      h4Headings.forEach(heading => {
        const categoryName = heading.textContent.trim();
        const attributes = [];

        // Collect li elements until next h3 or h4
        let currentNode = heading.nextElementSibling;

        while (currentNode) {
          const tagName = currentNode.tagName.toLowerCase();

          // Stop if we hit another heading
          if (tagName === 'h3' || tagName === 'h4') {
            break;
          }

          // Extract attributes from li elements
          if (tagName === 'ul' || tagName === 'ol') {
            const listItems = currentNode.querySelectorAll('li');
            listItems.forEach(li => {
              const attribute = this.parseAttributeFromListItem(li);
              if (attribute) {
                attributes.push(attribute);
              }
            });
          } else if (tagName === 'li') {
            const attribute = this.parseAttributeFromListItem(currentNode);
            if (attribute) {
              attributes.push(attribute);
            }
          }

          currentNode = currentNode.nextElementSibling;
        }

        // Only add category if it has attributes
        if (attributes.length > 0) {
          categories.push({
            name: categoryName,
            attributes,
            id: this.slugify(categoryName)
          });
        }
      });

      return categories;
    }

    /**
     * Parse a single li element to extract label and value
     * Handles patterns: "Label: Value" or "<strong>Label:</strong> Value"
     * @param {HTMLElement} li - List item element
     * @returns {Object|null} { label, value, icon }
     */
    parseAttributeFromListItem(li) {
      if (!li || !li.textContent) {
        return null;
      }

      // Get text content
      let text = '';
      let label = '';
      let value = '';

      // Check if there's a strong tag for the label
      const strong = li.querySelector('strong');

      if (strong) {
        label = strong.textContent.trim().replace(/:\s*$/, ''); // Remove trailing colon

        // Get remaining text after strong tag
        const fullText = li.textContent;
        const strongText = strong.textContent;
        value = fullText.substring(fullText.indexOf(strongText) + strongText.length).trim();

        // Remove leading colon or hyphen from value
        value = value.replace(/^[:–—-]\s*/, '');
      } else {
        // No strong tag, split on first colon
        text = li.textContent.trim();
        const colonIndex = text.indexOf(':');

        if (colonIndex !== -1) {
          label = text.substring(0, colonIndex).trim();
          value = text.substring(colonIndex + 1).trim();
        } else {
          // No colon found, treat entire text as label
          label = text;
          value = '';
        }
      }

      // Only return if we have a label
      if (!label) {
        return null;
      }

      return {
        label,
        value,
        icon: this.getIconForAttribute(label)
      };
    }

    /**
     * Get icon name for an attribute based on label matching
     * @param {string} label - Attribute label
     * @returns {string} Tabler icon name
     */
    getIconForAttribute(label) {
      const normalised = label.toLowerCase();

      for (const [keywords, icon] of Object.entries(this.ICON_MAP)) {
        if (keywords === 'default') continue;

        const keywordList = keywords.split('|');
        if (keywordList.some(keyword => normalised.includes(keyword))) {
          return icon;
        }
      }

      return this.ICON_MAP['default'];
    }

    /**
     * Convert string to slug for IDs
     * @param {string} text - Text to slugify
     * @returns {string} Slugified text
     */
    slugify(text) {
      return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

    /**
     * Select hero features (top 3-5 most important)
     * Priority scoring based on keywords and feature importance
     * @param {Array} features - All standout features
     * @returns {Object} { hero: [], remaining: [] }
     */
    selectHeroFeatures(features) {
      // Priority keywords (higher score = more important)
      const priorityKeywords = {
        'battery|power|voltage': 100,
        'drive|4wd|awd|wheel': 95,
        'official|brand|licensed|authentic': 90,
        'bluetooth|connectivity|smart': 85,
        'remote|control|parental': 85,
        'formula|one|f1': 90,
        'suspension': 80,
        'seat|leather|padded': 75,
        'lights|led': 70,
        'tyres|wheels|eva': 75,
        'multimedia|media|console': 70,
        'sounds|engine': 65,
        'belt|safety': 80
      };

      // Score each feature
      const scoredFeatures = features.map(feature => {
        const nameLower = feature.name.toLowerCase();
        let score = 0;

        // Check priority keywords
        for (const [keywords, points] of Object.entries(priorityKeywords)) {
          const keywordList = keywords.split('|');
          if (keywordList.some(k => nameLower.includes(k))) {
            score += points;
            break; // Only count highest matching keyword
          }
        }

        // Bonus for upgrade options (shows premium features)
        if (nameLower.includes('upgrade')) {
          score += 10;
        }

        return { ...feature, score };
      });

      // Sort by score descending
      scoredFeatures.sort((a, b) => b.score - a.score);

      // Select top 5 for hero, rest for "View All"
      const hero = scoredFeatures.slice(0, 5);
      const remaining = scoredFeatures.slice(5);

      return { hero, remaining };
    }

    /**
     * Select quick specs (top 6-8 most important specs)
     * Extract key specs from categories
     * @param {Array} categories - All attribute categories
     * @returns {Object} { quickSpecs: [], remainingCategories: [] }
     */
    selectQuickSpecs(categories) {
      // Priority spec names (must-know before purchase)
      const prioritySpecs = [
        'battery',
        'seat capacity',
        'speed',
        'age',
        'runtime',
        'charge time',
        'motors',
        'weight'
      ];

      const quickSpecs = [];
      const processedCategories = new Set();

      // Extract priority specs from all categories
      categories.forEach(category => {
        category.attributes.forEach(attr => {
          const labelLower = attr.label.toLowerCase();

          // Check if this is a priority spec
          if (prioritySpecs.some(ps => labelLower.includes(ps)) && quickSpecs.length < 8) {
            quickSpecs.push({
              label: attr.label,
              value: attr.value,
              icon: attr.icon,
              categoryId: category.id
            });
            processedCategories.add(category.id);
          }
        });
      });

      return {
        quickSpecs,
        remainingCategories: categories
      };
    }

    /**
     * Render widget to container
     * Uses setContent() to properly handle skeleton loading and transitions
     */
    render() {
      // Only show widget if we have features or categories
      if (this.standoutFeatures.length === 0 && this.attributeCategories.length === 0) {
        this.container.style.display = 'none';
        return;
      }

      // Build widget HTML and set content (replaces skeleton)
      this.setContent(this.buildWidgetHTML());

      // Setup read more functionality
      this.setupReadMore();

      // Attach event listeners
      this.attachEventListeners();

      // Build and append specs modal (after HTML is built and specsCategories is set)
      this.buildSpecsModal();

      // Mark as rendered
      this.rendered = true;

      // Emit render event
      this.emit('features-showcase:rendered', {
        standoutFeatures: this.standoutFeatures.length,
        categories: this.attributeCategories.length
      });
    }

    /**
     * Build complete widget HTML
     * @returns {string} HTML string
     */
    buildWidgetHTML() {
      // Split features into hero and remaining
      const { hero, remaining } = this.standoutFeatures.length > 0
        ? this.selectHeroFeatures(this.standoutFeatures)
        : { hero: [], remaining: [] };

      // Select quick specs (top 6-8 most important)
      const { quickSpecs, remainingCategories } = this.attributeCategories.length > 0
        ? this.selectQuickSpecs(this.attributeCategories)
        : { quickSpecs: [], remainingCategories: [] };

      return `
        <div class="pw-widget pw-features-showcase">
          <div class="pw-features-showcase__content" data-collapsed-height="${this.collapsedHeight}">
            ${hero.length > 0 ? this.renderHeroFeatures(hero) : ''}
            ${quickSpecs.length > 0 ? this.renderQuickSpecs(quickSpecs) : ''}
            ${remaining.length > 0 ? this.renderRemainingFeatures(remaining) : ''}
            ${remainingCategories.length > 0 ? this.renderRemainingSpecs(remainingCategories) : ''}
          </div>
          <button class="pw-features-showcase__toggle" type="button" data-pw-toggle>
            <span class="pw-toggle-text">Read More</span>
            <span class="pw-toggle-icon">${this.getChevronDownIcon()}</span>
          </button>
        </div>
      `;
    }

    /**
     * Setup read more functionality
     * Checks if content exceeds collapsed height and shows/hides toggle
     */
    setupReadMore() {
      const content = this.container.querySelector('.pw-features-showcase__content');
      const toggle = this.container.querySelector('.pw-features-showcase__toggle');

      if (!content || !toggle) return;

      // Measure full height
      const fullHeight = content.scrollHeight;
      const collapsedHeight = this.collapsedHeight;

      if (fullHeight > collapsedHeight + 50) {
        // Content needs collapsing - show toggle
        toggle.style.display = 'flex';

        // Set initial collapsed state
        content.style.maxHeight = `${collapsedHeight}px`;
        content.classList.remove('is-expanded');

        // Attach toggle listener
        this.toggleButtonClickHandler = () => this.toggleExpanded();
        toggle.addEventListener('click', this.toggleButtonClickHandler);
      } else {
        // Content fits - hide toggle and show all
        toggle.style.display = 'none';
        content.classList.add('is-expanded');
        content.style.maxHeight = 'none';
      }
    }

    /**
     * Toggle expanded/collapsed state
     */
    toggleExpanded() {
      const wasExpanded = this.isExpanded;
      this.isExpanded = !this.isExpanded;
      this.updateExpandedState();

      // When collapsing, scroll back to widget top if user scrolled past it
      if (wasExpanded && !this.isExpanded) {
        this.scrollToWidgetTop();
      }
    }

    /**
     * Scroll to widget top when collapsing
     */
    scrollToWidgetTop() {
      // Use the container element directly
      const widgetTop = this.container.getBoundingClientRect().top + window.scrollY;

      // Scroll to widget with some offset for header
      window.scrollTo({
        top: Math.max(0, widgetTop - 80),
        behavior: 'smooth'
      });
    }

    /**
     * Update UI based on expanded state
     */
    updateExpandedState() {
      const content = this.container.querySelector('.pw-features-showcase__content');
      const toggle = this.container.querySelector('.pw-features-showcase__toggle');
      const toggleText = toggle?.querySelector('.pw-toggle-text');
      const toggleIcon = toggle?.querySelector('.pw-toggle-icon');

      if (!content || !toggle) return;

      if (this.isExpanded) {
        content.classList.add('is-expanded');
        content.style.maxHeight = `${content.scrollHeight}px`;
        if (toggleText) toggleText.textContent = 'Read Less';
        if (toggleIcon) toggleIcon.innerHTML = this.getChevronUpIcon();
      } else {
        content.classList.remove('is-expanded');
        content.style.maxHeight = `${this.collapsedHeight}px`;
        if (toggleText) toggleText.textContent = 'Read More';
        if (toggleIcon) toggleIcon.innerHTML = this.getChevronDownIcon();
      }
    }

    /**
     * Chevron down icon
     */
    getChevronDownIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    /**
     * Chevron up icon
     */
    getChevronUpIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 10L8 6L4 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    /**
     * Render hero features (top 3-5 features with large cards)
     * @param {Array} features - Hero features
     * @returns {string} HTML string
     */
    renderHeroFeatures(features) {
      return `
        <div class="pw-hero-features">
          <h2 class="pw-hero-features__title">What Makes This Special</h2>
          <div class="pw-hero-features__grid">
            ${features.map((feature, index) => `
              <div class="pw-hero-card" style="animation-delay: ${index * 0.1}s">
                <div class="pw-hero-card__icon-wrapper">
                  <i class="ti ti-${feature.icon}" aria-hidden="true"></i>
                </div>
                <h3 class="pw-hero-card__title">${this.escapeHtml(feature.name)}</h3>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Render quick specs (top 6-8 essential specs in compact grid)
     * @param {Array} specs - Quick specs
     * @returns {string} HTML string
     */
    renderQuickSpecs(specs) {
      return `
        <div class="pw-quick-specs">
          <h3 class="pw-quick-specs__title">Key Specifications</h3>
          <div class="pw-quick-specs__grid">
            ${specs.map((spec, index) => `
              <div class="pw-quick-spec-card" style="animation-delay: ${index * 0.05}s">
                <i class="ti ti-${spec.icon}" aria-hidden="true"></i>
                <div class="pw-quick-spec-card__content">
                  <span class="pw-quick-spec-card__label">${this.escapeHtml(spec.label)}</span>
                  <span class="pw-quick-spec-card__value">${this.escapeHtml(spec.value)}</span>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Render remaining features (collapsible)
     * @param {Array} features - Remaining features
     * @returns {string} HTML string
     */
    renderRemainingFeatures(features) {
      return `
        <details class="pw-collapsible pw-collapsible--features">
          <summary class="pw-collapsible__summary">
            <span class="pw-collapsible__title">
              <i class="ti ti-list" aria-hidden="true"></i>
              All Features
            </span>
            <span class="pw-collapsible__count">${features.length} more</span>
            <i class="ti ti-chevron-down pw-collapsible__arrow" aria-hidden="true"></i>
          </summary>
          <div class="pw-collapsible__content">
            <div class="pw-feature-chips">
              ${features.map(feature => `
                <div class="pw-feature-chip">
                  <i class="ti ti-${feature.icon}" aria-hidden="true"></i>
                  <span>${this.escapeHtml(feature.name)}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </details>
      `;
    }

    /**
     * Render remaining specs (collapsible with simple cards, search and filters)
     * @param {Array} categories - Remaining specification categories
     * @returns {string} HTML string
     */
    renderRemainingSpecs(categories) {
      // Store category icon mapping
      categories.forEach(category => {
        if (!category.icon) {
          category.icon = this.getIconForAttribute(category.name);
        }
      });

      this.specsCategories = categories;
      const specsCount = categories.reduce((sum, cat) => sum + cat.attributes.length, 0);
      const modalId = `specs-modal-${this.config.id}`;
      this.modalId = modalId;

      return `
        <div class="pw-collapsible pw-collapsible--specs">
          <button
            type="button"
            class="pw-collapsible__summary"
            aria-controls="${modalId}"
            aria-expanded="false"
            style="width: 100%; cursor: pointer;"
          >
            <span class="pw-collapsible__title">
              <i class="ti ti-list-details" aria-hidden="true"></i>
              Complete Specifications
            </span>
            <span class="pw-collapsible__count">${specsCount} specs</span>
            <i class="ti ti-arrow-right pw-collapsible__arrow" aria-hidden="true"></i>
          </button>
        </div>
      `;
    }


    /**
     * Build modal for complete specifications
     */
    buildSpecsModal() {
      if (!this.specsCategories || this.specsCategories.length === 0) return;

      const categories = this.specsCategories;
      const specsCount = categories.reduce((sum, cat) => sum + cat.attributes.length, 0);
      const modalId = this.modalId;

      const modalHTML = `
        <x-modal id="${modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Complete Specifications" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Complete Specifications</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">

                <!-- Hero Section -->
                <div style="text-align: center;">
                  <div style="display: inline-flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; background: rgb(var(--color-base-button)); border-radius: 50%; margin-bottom: 1rem;">
                    <i class="ti ti-list-details" style="font-size: 2rem; color: white;"></i>
                  </div>
                  <h3 class="heading font-bold text-xl md:text-2xl" style="margin-bottom: 0.75rem;">
                    ${specsCount} Technical Specifications
                  </h3>
                  <p class="text-base" style="max-width: 600px; margin: 0 auto 1rem auto; color: rgb(var(--color-drawer-text) / 0.85);">
                    Complete technical details to help you make an informed purchase decision.
                  </p>
                </div>

                <!-- Search/Filter Header -->
                <div class="pw-specs-header">
                  ${categories.length > 1 ? `
                    <div class="pw-specs-filter-dropdown">
                      <button
                        class="pw-specs-filter-dropdown__button"
                        data-pw-filter-dropdown-button
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        <span data-pw-filter-label>All Categories</span>
                        <i class="ti ti-chevron-down" aria-hidden="true"></i>
                      </button>
                      <div class="pw-specs-filter-dropdown__menu" data-pw-filter-dropdown-menu hidden>
                        <div class="pw-specs-filter-dropdown__option" data-pw-filter="all">
                          <i class="ti ti-layout-grid" aria-hidden="true"></i>
                          <span>All Categories</span>
                          <i class="ti ti-check pw-specs-filter-dropdown__check" aria-hidden="true"></i>
                        </div>
                        ${categories.map(category => `
                          <div class="pw-specs-filter-dropdown__option" data-pw-filter="${category.id}">
                            <i class="ti ti-${category.icon}" aria-hidden="true"></i>
                            <span>${this.escapeHtml(this.removeEmojis(category.name))}</span>
                            <i class="ti ti-check pw-specs-filter-dropdown__check" aria-hidden="true"></i>
                          </div>
                        `).join('')}
                      </div>
                    </div>
                  ` : ''}

                  <div class="pw-specs-search">
                    <input
                      type="search"
                      class="pw-specs-search__input"
                      placeholder="Search specifications..."
                      aria-label="Search specifications"
                      data-pw-search-input
                    />
                    <button
                      type="button"
                      class="pw-specs-search__clear"
                      aria-label="Clear search"
                      data-pw-search-clear
                    >
                      <i class="ti ti-circle-x" aria-hidden="true"></i>
                    </button>
                    <i class="ti ti-search pw-specs-search__icon" aria-hidden="true"></i>
                  </div>
                </div>

                <!-- Specs Content -->
                <div class="pw-specs-content" data-pw-specs-content>
                  ${categories.map(category => `
                    <div class="pw-spec-category" data-category-id="${category.id}">
                      <h4 class="pw-spec-category__title">
                        <i class="ti ti-${category.icon}" aria-hidden="true"></i>
                        ${this.escapeHtml(this.removeEmojis(category.name))}
                      </h4>
                      <div class="pw-spec-list">
                        ${category.attributes.map(attr => `
                          <div class="pw-spec-item" data-label="${this.escapeHtml(attr.label.toLowerCase())}" data-value="${this.escapeHtml(attr.value.toLowerCase())}">
                            <span class="pw-spec-item__label">${this.escapeHtml(attr.label)}</span>
                            <span class="pw-spec-item__value">${this.escapeHtml(attr.value)}</span>
                          </div>
                        `).join('')}
                      </div>
                    </div>
                  `).join('')}
                </div>

              </div>
            </div>
          </div>
        </x-modal>
      `;

      // Append to body
      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalHTML;
      const modal = modalElement.firstElementChild;
      document.body.appendChild(modal);

      // Attach event listeners to modal elements
      this.attachModalEventListeners(modal);
    }



    /**
     * OLD METHOD - Kept for reference but not used
     * Render standout features section (icon-only cards)
     * @returns {string} HTML string
     */
    renderStandoutFeatures() {
      return `
        <div class="pw-features-showcase__standout">
          <h3 class="pw-features-showcase__title">Key Features</h3>
          <div class="pw-features-showcase__cards">
            ${this.standoutFeatures.map(feature => `
              <div class="pw-feature-card">
                <i class="ti ti-${feature.icon}" aria-hidden="true"></i>
                <span>${this.escapeHtml(feature.name)}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Render specifications grid section
     * @returns {string} HTML string
     */
    renderSpecsGrid() {
      return `
        <div class="pw-features-showcase__specs">
          <div class="pw-specs-header">
            <h3 class="pw-specs-header__title">Technical Specifications</h3>

            <div class="pw-specs-search">
              <i class="ti ti-search" aria-hidden="true"></i>
              <input
                type="search"
                class="pw-specs-search__input"
                placeholder="Search specifications..."
                aria-label="Search specifications"
                data-pw-search-input
              />
            </div>

            ${this.attributeCategories.length > 1 ? `
              <div class="pw-specs-filters" role="group" aria-label="Category filters">
                <button
                  class="pw-specs-filter-chip pw-specs-filter-chip--active"
                  data-pw-filter="all"
                  aria-pressed="true"
                >
                  <i class="ti ti-layout-grid" aria-hidden="true"></i>
                  <span>All Specs</span>
                </button>
                ${this.attributeCategories.map(category => `
                  <button
                    class="pw-specs-filter-chip"
                    data-pw-filter="${category.id}"
                    aria-pressed="false"
                  >
                    <i class="ti ${category.icon}" aria-hidden="true"></i>
                    <span>${this.escapeHtml(category.name)}</span>
                  </button>
                `).join('')}
              </div>
            ` : ''}
          </div>

          <div class="pw-specs-content">
            ${this.filteredCategories.length > 0 ?
              this.filteredCategories.map(category => this.renderCategory(category)).join('') :
              `<div class="pw-specs-no-results">
                <i class="ti ti-search-off" aria-hidden="true"></i>
                <p>No specifications found matching "${this.escapeHtml(this.searchTerm)}"</p>
              </div>`
            }
          </div>
        </div>
      `;
    }

    /**
     * Render a single category section
     * @param {Object} category - Category object
     * @returns {string} HTML string
     */
    renderCategory(category) {
      return `
        <div class="pw-specs-category" data-category="${category.id}">
          <h4 class="pw-specs-category__title">${this.escapeHtml(category.name)}</h4>
          <div class="pw-specs-grid">
            ${category.attributes.map(attr => `
              <div class="pw-spec-item">
                <div class="pw-spec-item__icon">
                  <i class="ti ti-${attr.icon}" aria-hidden="true"></i>
                </div>
                <div class="pw-spec-item__content">
                  <div class="pw-spec-item__label">${this.escapeHtml(attr.label)}</div>
                  ${attr.value ? `<div class="pw-spec-item__value">${this.escapeHtml(attr.value)}</div>` : ''}
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Attach event listeners for search and filters
     */
    attachEventListeners() {
      // NOTE: Modal trigger button uses aria-controls="${modalId}" - theme handles click automatically
      // No manual click listener needed for modal opening/closing

      // Search input
      const searchInput = this.container.querySelector('[data-pw-search-input]');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => {
          this.handleSearchInput(e.target.value);
        });
      }

      // Clear button
      const clearButton = this.container.querySelector('[data-pw-search-clear]');
      if (clearButton && searchInput) {
        clearButton.addEventListener('click', () => {
          searchInput.value = '';
          this.handleSearchInput('');
          searchInput.focus();
        });
      }

      // Filter dropdown button
      const dropdownButton = this.container.querySelector('[data-pw-filter-dropdown-button]');
      const dropdownMenu = this.container.querySelector('[data-pw-filter-dropdown-menu]');

      if (dropdownButton && dropdownMenu) {
        // Toggle dropdown
        dropdownButton.addEventListener('click', (e) => {
          e.stopPropagation();
          const isExpanded = dropdownButton.getAttribute('aria-expanded') === 'true';
          dropdownButton.setAttribute('aria-expanded', !isExpanded);
          dropdownMenu.hidden = isExpanded;
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
          if (!dropdownButton.contains(e.target) && !dropdownMenu.contains(e.target)) {
            dropdownButton.setAttribute('aria-expanded', 'false');
            dropdownMenu.hidden = true;
          }
        });

        // Filter options
        const filterOptions = dropdownMenu.querySelectorAll('[data-pw-filter]');
        filterOptions.forEach(option => {
          option.addEventListener('click', (e) => {
            e.stopPropagation();
            const filterValue = option.dataset.pwFilter;
            this.handleFilterClick(filterValue); // handleFilterClick now calls updateDropdownUI internally
          });
        });
      }
    }

    /**
     * Attach event listeners to modal elements
     * @param {HTMLElement} modal - Modal element
     */
    attachModalEventListeners(modal) {
      // NOTE: Close button and overlay use aria-controls - theme handles closing automatically
      // We only need to attach listeners for search/filter functionality

      // Search input in modal
      const searchInput = modal.querySelector('[data-pw-search-input]');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => {
          this.handleSearchInput(e.target.value);
        });
      }

      // Clear button in modal
      const clearButton = modal.querySelector('[data-pw-search-clear]');
      if (clearButton && searchInput) {
        clearButton.addEventListener('click', () => {
          searchInput.value = '';
          this.handleSearchInput('');
          searchInput.focus();
        });
      }

      // Filter dropdown button in modal
      const dropdownButton = modal.querySelector('[data-pw-filter-dropdown-button]');
      const dropdownMenu = modal.querySelector('[data-pw-filter-dropdown-menu]');

      if (dropdownButton && dropdownMenu) {
        // Toggle dropdown
        dropdownButton.addEventListener('click', (e) => {
          e.stopPropagation();
          const isExpanded = dropdownButton.getAttribute('aria-expanded') === 'true';
          dropdownButton.setAttribute('aria-expanded', !isExpanded);
          dropdownMenu.hidden = isExpanded;
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
          if (!dropdownButton.contains(e.target) && !dropdownMenu.contains(e.target)) {
            dropdownButton.setAttribute('aria-expanded', 'false');
            dropdownMenu.hidden = true;
          }
        });

        // Filter options
        const filterOptions = dropdownMenu.querySelectorAll('[data-pw-filter]');
        filterOptions.forEach(option => {
          option.addEventListener('click', (e) => {
            e.stopPropagation();
            const filterValue = option.dataset.pwFilter;
            this.handleFilterClick(filterValue); // handleFilterClick now calls updateDropdownUI internally
          });
        });
      }
    }

    /**
     * Handle search input with debouncing
     * @param {string} searchValue - Search input value
     */
    handleSearchInput(searchValue) {
      // Clear existing timeout
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }

      // Debounce search by 100ms
      this.searchDebounceTimeout = setTimeout(() => {
        this.searchTerm = searchValue.toLowerCase().trim();
        this.applyFilters();
      }, 100);
    }

    /**
     * Handle filter button click
     * @param {string} filterValue - Filter ID or 'all'
     */
    handleFilterClick(filterValue) {
      // Update selected category (single selection only)
      this.selectedCategory = filterValue;

      // Update dropdown UI to show selection
      this.updateDropdownUI();

      // Close the dropdown (check both container and modal)
      const modal = document.getElementById(this.modalId);
      const context = modal || this.container;
      const dropdownButton = context.querySelector('[data-pw-filter-dropdown-button]');
      const dropdownMenu = context.querySelector('[data-pw-filter-dropdown-menu]');

      if (dropdownButton && dropdownMenu) {
        dropdownButton.setAttribute('aria-expanded', 'false');
        dropdownMenu.hidden = true;
      }

      // Apply category filter and search
      this.applyFilters();
    }

    /**
     * Update visual state of filter buttons (deprecated - kept for compatibility)
     */
    updateFilterButtonStates() {
      // Legacy method - now handled by updateDropdownUI
      this.updateDropdownUI();
    }

    /**
     * Update dropdown UI to show selected category (works for both container and modal)
     */
    updateDropdownUI() {
      // Check both container and modal for dropdown elements
      const modal = document.getElementById(this.modalId);
      const contexts = [this.container];
      if (modal) contexts.push(modal);

      contexts.forEach(context => {
        const dropdownMenu = context.querySelector('[data-pw-filter-dropdown-menu]');
        const dropdownLabel = context.querySelector('[data-pw-filter-label]');

        if (!dropdownMenu || !dropdownLabel) return;

        const filterOptions = dropdownMenu.querySelectorAll('[data-pw-filter]');

        // Update active state - only one option can be active at a time
        filterOptions.forEach(option => {
          const filterValue = option.dataset.pwFilter;
          const isActive = filterValue === this.selectedCategory;

          if (isActive) {
            option.classList.add('pw-specs-filter-dropdown__option--active');
          } else {
            option.classList.remove('pw-specs-filter-dropdown__option--active');
          }
        });

        // Update label text to show selected category
        if (this.selectedCategory === 'all') {
          dropdownLabel.textContent = 'All Categories';
        } else {
          const activeOption = dropdownMenu.querySelector(`[data-pw-filter="${this.selectedCategory}"]`);
          if (activeOption) {
            const spanElement = activeOption.querySelector('span');
            dropdownLabel.textContent = spanElement ? spanElement.textContent : 'Category';
          }
        }
      });
    }

    /**
     * Apply search and category filter to specifications (single-select filter)
     */
    applyFilters() {
      // Try to find specs content in modal first, fallback to container
      const modal = document.getElementById(this.modalId);
      const specsContent = modal
        ? modal.querySelector('[data-pw-specs-content]')
        : this.container.querySelector('[data-pw-specs-content]');
      if (!specsContent) return;

      const categories = specsContent.querySelectorAll('[data-category-id]');
      let visibleCount = 0;

      categories.forEach(categoryEl => {
        const categoryId = categoryEl.dataset.categoryId;
        const specItems = categoryEl.querySelectorAll('.pw-spec-item');
        let categoryHasVisibleItems = false;

        // Check if category matches selected filter (single selection)
        const categoryVisible = this.selectedCategory === 'all' || this.selectedCategory === categoryId;

        // Check each spec item for search match
        specItems.forEach(item => {
          const label = item.dataset.label || '';
          const value = item.dataset.value || '';
          const searchMatch = !this.searchTerm ||
            label.includes(this.searchTerm) ||
            value.includes(this.searchTerm);

          const itemVisible = categoryVisible && searchMatch;

          if (itemVisible) {
            item.style.display = '';
            categoryHasVisibleItems = true;
          } else {
            item.style.display = 'none';
          }
        });

        // Show/hide entire category based on filter and whether it has visible items
        if (categoryHasVisibleItems) {
          categoryEl.style.display = '';
          visibleCount++;
        } else {
          categoryEl.style.display = 'none';
        }
      });

      // Show "no results" message if nothing visible
      let noResultsEl = specsContent.querySelector('.pw-specs-no-results');

      if (visibleCount === 0) {
        if (!noResultsEl) {
          noResultsEl = document.createElement('div');
          noResultsEl.className = 'pw-specs-no-results';
          noResultsEl.innerHTML = `
            <i class="ti ti-search-off" aria-hidden="true"></i>
            <p>No specifications found</p>
          `;
          specsContent.appendChild(noResultsEl);
        }
        noResultsEl.style.display = 'block';
      } else {
        if (noResultsEl) {
          noResultsEl.style.display = 'none';
        }
      }
    }

    /**
     * Remove emojis from text
     * @param {string} text - Text to clean
     * @returns {string} Text without emojis
     */
    removeEmojis(text) {
      if (!text) return '';
      // Remove emojis using regex
      return text.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]/gu, '').trim();
    }

    /**
     * Escape HTML special characters to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Cleanup widget
     */
    destroy() {
      // Clear debounce timeout
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
      }

      // Call parent destroy
      super.destroy();
    }
  }

  // Register widget
  if (!window.ProductWidgets) {
    window.ProductWidgets = { widgets: {} };
  }

  window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  window.ProductWidgets.widgets.FeaturesShowcaseWidget = FeaturesShowcaseWidget;

})(window);
